// 新建知识库（沿用现有库的模型、切分、索引设置）与知识库设置（改名字和说明、删除）。

import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App as AntApp, Modal, Switch } from 'antd'
import { createKnowledgeBase, deleteKnowledgeBase, getKnowledgeBaseRaw, updateKnowledgeBase } from '../../api/kb'
import { listModels, modelLabel } from '../../api/settings'
import type { KnowledgeBase } from '../../api/types'
import { Pill } from '../../ui'
import './dialogs.css'

const modelName = (models: { id: string; name: string; display_name?: string }[], id?: unknown) => {
  const m = models.find((x) => x.id === id)
  return m ? modelLabel(models, m) : typeof id === 'string' && id ? id : '默认'
}

export function KbCreateDialog({ kbs, onClose }: { kbs: KnowledgeBase[]; onClose: () => void }) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { message } = AntApp.useApp()
  const source = kbs.find((k) => k.type !== 'faq')
  const template = useQuery({ queryKey: ['kb-raw', source?.id], enabled: !!source, queryFn: () => getKnowledgeBaseRaw(source!.id) })
  const models = useQuery({ queryKey: ['models'], queryFn: listModels, staleTime: 5 * 60_000 }).data ?? []
  const t = template.data ?? {}
  const strategy = (t.indexing_strategy as Record<string, boolean> | undefined) ?? {}
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [wiki, setWiki] = useState<boolean | null>(null)
  const [graph, setGraph] = useState<boolean | null>(null)
  const wikiOn = wiki ?? !!strategy.wiki_enabled
  const graphOn = graph ?? !!strategy.graph_enabled
  const chunk = (t.chunking_config as { chunk_size?: number; chunk_overlap?: number } | undefined) ?? {}
  const create = useMutation({
    mutationFn: () => createKnowledgeBase({ name: name.trim(), description: description.trim(), wiki: wikiOn, graph: graphOn }, t),
    onSuccess: (kb) => {
      qc.invalidateQueries({ queryKey: ['knowledge-bases'] })
      message.success(`建好了「${kb.name}」`)
      onClose()
      navigate(`/kb/${kb.id}`)
    },
    onError: (e) => void message.error(e.message),
  })
  return (
    <Modal open title="新建知识库" onCancel={onClose} footer={null} destroyOnHidden>
      <form
        className="dlg"
        onSubmit={(e) => {
          e.preventDefault()
          if (name.trim()) create.mutate()
        }}
      >
        <label htmlFor="kb-name">名字</label>
        <input id="kb-name" value={name} maxLength={100} onChange={(e) => setName(e.target.value)} autoFocus />
        <label htmlFor="kb-desc">说明（可选）</label>
        <textarea id="kb-desc" rows={2} value={description} maxLength={500} onChange={(e) => setDescription(e.target.value)} />
        {source && (
          <div className="dlg-note" aria-label="沿用的设置">
            <p>沿用「{source.name}」的设置：</p>
            <ul>
              <li>向量模型：{modelName(models, t.embedding_model_id)}</li>
              <li>整理用的模型：{modelName(models, t.summary_model_id)}</li>
              {chunk.chunk_size ? <li>切分：每段 {chunk.chunk_size} 字{chunk.chunk_overlap ? `，重叠 ${chunk.chunk_overlap} 字` : ''}</li> : null}
            </ul>
          </div>
        )}
        <div className="dlg-switch">
          <span>
            自动整理 Wiki<small>每篇资料解析后，用模型整理出概念、实体和摘要（会调用模型）</small>
          </span>
          <Switch checked={wikiOn} onChange={setWiki} aria-label="自动整理 Wiki" />
        </div>
        <div className="dlg-switch">
          <span>
            关联图<small>抽取资料里的实体关系（会调用模型）</small>
          </span>
          <Switch checked={graphOn} onChange={setGraph} aria-label="关联图" />
        </div>
        <div className="dlg-acts">
          <Pill type="submit" variant="primary" disabled={!name.trim() || create.isPending || (!!source && template.isPending)}>
            新建
          </Pill>
          <Pill onClick={onClose}>取消</Pill>
        </div>
      </form>
    </Modal>
  )
}

export function KbSettingsDialog({ kb, onClose }: { kb: KnowledgeBase; onClose: () => void }) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { message } = AntApp.useApp()
  const [name, setName] = useState(kb.name)
  const [description, setDescription] = useState(kb.description ?? '')
  const [confirm, setConfirm] = useState('')
  const [deleting, setDeleting] = useState(false)
  const count = kb.knowledge_count ?? 0
  const save = useMutation({
    mutationFn: () => updateKnowledgeBase(kb.id, name.trim(), description.trim()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['knowledge-bases'] })
      qc.invalidateQueries({ queryKey: ['knowledge-base', kb.id] })
      message.success('改好了')
      onClose()
    },
    onError: (e) => void message.error(e.message),
  })
  const remove = useMutation({
    mutationFn: () => deleteKnowledgeBase(kb.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['knowledge-bases'] })
      message.success(`删掉了「${kb.name}」`)
      onClose()
      navigate('/kb', { replace: true })
    },
    onError: (e) => void message.error(e.message),
  })
  const canDelete = count === 0 || confirm === kb.name
  return (
    <Modal open title="知识库设置" onCancel={onClose} footer={null} destroyOnHidden>
      <form
        className="dlg"
        onSubmit={(e) => {
          e.preventDefault()
          if (name.trim()) save.mutate()
        }}
      >
        <label htmlFor="kbs-name">名字</label>
        <input id="kbs-name" value={name} maxLength={100} onChange={(e) => setName(e.target.value)} />
        <label htmlFor="kbs-desc">说明</label>
        <textarea id="kbs-desc" rows={2} value={description} maxLength={500} onChange={(e) => setDescription(e.target.value)} />
        <div className="dlg-acts">
          <Pill type="submit" variant="primary" disabled={!name.trim() || save.isPending || (name.trim() === kb.name && description.trim() === (kb.description ?? ''))}>
            保存
          </Pill>
          <Pill onClick={onClose}>取消</Pill>
        </div>
      </form>
      <section className="dlg-danger" aria-label="删除知识库">
        <h3>删除知识库</h3>
        {!deleting ? (
          <>
            <p>{count ? `里面的 ${count} 篇资料、Wiki 和对话里对它的引用都会一起删掉，不能恢复。` : '这个库是空的。'}</p>
            <Pill size="sm" onClick={() => setDeleting(true)}>
              删除这个知识库…
            </Pill>
          </>
        ) : (
          <>
            {count > 0 && (
              <>
                <label htmlFor="kbs-confirm">输入库名「{kb.name}」确认</label>
                <input id="kbs-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="off" />
              </>
            )}
            <div className="dlg-acts">
              <Pill size="sm" className="danger" disabled={!canDelete || remove.isPending} onClick={() => remove.mutate()}>
                确认删除
              </Pill>
              <Pill size="sm" onClick={() => setDeleting(false)}>
                不删了
              </Pill>
            </div>
          </>
        )}
      </section>
    </Modal>
  )
}
